// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {ChannelHeaderIcon} from '@app/features/channel/components/channel_header_components/ChannelHeaderIcon';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import {CompanionRecordDetail} from '@app/features/companion/components/records/CompanionRecordDetail';
import recordStyles from '@app/features/companion/components/records/CompanionRecords.module.css';
import {CompanionRecordsSkeleton} from '@app/features/companion/components/records/CompanionRecordsSkeleton';
import {CompanionSessionsList} from '@app/features/companion/components/records/CompanionSessionsList';
import {CompanionRecordsState} from '@app/features/companion/state/CompanionRecordsState';
import styles from '@app/features/discovery/discovery/DiscoveryCompanionRecordsPage.module.css';
import {GO_BACK_DESCRIPTOR, TRY_AGAIN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Navigation from '@app/features/navigation/state/Navigation';
import {goBackOr} from '@app/features/platform/components/router/NavigationAdapter';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {Button} from '@app/features/ui/button/Button';
import {Scroller} from '@app/features/ui/components/Scroller';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowLeftIcon, ClockCounterClockwiseIcon, WarningCircleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

const COMPANION_RECORDS_DESCRIPTOR = msg({
	message: 'Companion records',
	comment: 'Title of the companion history page in Discovery.',
});
const LOAD_SESSIONS_FAILED_DESCRIPTOR = msg({
	message: 'Unable to load companion sessions',
	comment: 'Error title when the companion history session list cannot be loaded in Discovery.',
});

interface DiscoveryCompanionRecordsPageProps {
	readonly sessionId: string | null;
}

export const DiscoveryCompanionRecordsPage = observer(function DiscoveryCompanionRecordsPage({
	sessionId,
}: DiscoveryCompanionRecordsPageProps) {
	const {i18n} = useLingui();
	const [store] = useState(() => new CompanionRecordsState());
	const [sceneNames, setSceneNames] = useState<Record<string, string>>({});
	useEffect(() => {
		let disposed = false;
		void CompanionCommands.listScenes()
			.then((catalog) => {
				if (!disposed) setSceneNames(Object.fromEntries(catalog.scenes.map((scene) => [scene.id, scene.name])));
			})
			.catch(() => {});
		return () => {
			disposed = true;
		};
	}, []);
	useEffect(() => {
		const unregister = AccountScopedWork.registerCancellation(store.reset);
		return () => {
			unregister();
			store.reset();
		};
	}, [store]);
	useEffect(() => {
		void store.loadSessions();
	}, [store]);
	useEffect(() => {
		if (sessionId) {
			void store.navigateToDetail(sessionId);
		} else {
			store.navigateToList();
		}
	}, [sessionId, store]);
	const handleSelectSession = useCallback((selectedSessionId: string) => {
		Navigation.navigateToCompanionRecord(selectedSessionId);
	}, []);
	const handleBack = useCallback(() => {
		goBackOr(Routes.DISCOVER_COMPANIONS);
	}, []);
	const handleDeleteSession = useCallback(async () => {
		const targetSessionId = store.selectedSessionId;
		if (!targetSessionId) return false;
		const deleted = await store.deleteSession(targetSessionId);
		if (deleted) {
			goBackOr(Routes.DISCOVER_COMPANIONS);
		}
		return deleted;
	}, [store]);
	const selectedSessionId = store.selectedSessionId;
	return (
		<div className={styles.container} data-flx="discovery.companion-records.container">
			<header className={styles.header} data-flx="discovery.companion-records.header">
				<div className={styles.headerContent} data-flx="discovery.companion-records.header-content">
					{MobileLayout.enabled && !selectedSessionId ? (
						<ChannelHeaderIcon
							icon={ArrowLeftIcon}
							iconWeight="bold"
							label={i18n._(GO_BACK_DESCRIPTOR)}
							onClick={() => Navigation.navigateToDiscover()}
							data-flx="discovery.companion-records.button.back-to-discovery"
						/>
					) : null}
					<ClockCounterClockwiseIcon
						className={styles.headerIcon}
						weight="fill"
						aria-hidden
						data-flx="discovery.companion-records.icon"
					/>
					<h1 className={styles.title} data-flx="discovery.companion-records.title">
						{i18n._(COMPANION_RECORDS_DESCRIPTOR)}
					</h1>
				</div>
			</header>
			<Scroller className={styles.scroller} data-flx="discovery.companion-records.scroller">
				<div className={styles.content} data-flx="discovery.companion-records.content">
					{selectedSessionId ? (
						<CompanionRecordDetail
							key={selectedSessionId}
							sessionId={selectedSessionId}
							sceneNames={sceneNames}
							detail={store.detail}
							recordings={store.recordings}
							isDetailLoading={store.isDetailLoading}
							isRecordingsLoading={store.isRecordingsLoading}
							detailError={store.detailError}
							recordingsError={store.recordingsError}
							generatingInsightKind={store.generatingInsightKind}
							insightError={store.insightError}
							isDeletingSession={store.isDeletingSession}
							deleteError={store.deleteError}
							onBack={handleBack}
							onRetryDetail={() => void store.loadDetail(selectedSessionId)}
							onRetryRecordings={() => void store.loadRecordings(selectedSessionId)}
							onGenerateInsight={(kind) => store.generateInsight(selectedSessionId, kind)}
							onToggleActionItem={(itemId, completed) => store.toggleActionItem(selectedSessionId, itemId, completed)}
							onDeleteSession={handleDeleteSession}
							data-flx="discovery.companion-records.detail"
						/>
					) : null}
					<section
						className={styles.historySection}
						hidden={!!selectedSessionId}
						aria-label={i18n._(COMPANION_RECORDS_DESCRIPTOR)}
						data-flx="discovery.discovery.discovery-companion-records-page.history-section"
					>
						<div className={styles.intro} data-flx="discovery.discovery.discovery-companion-records-page.intro">
							<h2
								className={styles.introTitle}
								data-flx="discovery.discovery.discovery-companion-records-page.intro-title"
							>
								{i18n._(msg({message: 'Your companion sessions'}))}
								{store.sessions.length > 0 ? (
									<span data-flx="discovery.discovery.discovery-companion-records-page.intro-title-count">
										{' · '}
										{store.sessions.length}
									</span>
								) : null}
							</h2>
							<p
								className={styles.introDescription}
								data-flx="discovery.discovery.discovery-companion-records-page.intro-description"
							>
								{i18n._(msg({message: 'Revisit conversations, read transcripts, and listen to recordings.'}))}
							</p>
						</div>
						{store.isListLoading && store.sessions.length === 0 ? (
							<CompanionRecordsSkeleton data-flx="discovery.discovery.discovery-companion-records-page.companion-records-skeleton" />
						) : store.listError && store.sessions.length === 0 ? (
							<StatusSlate
								Icon={WarningCircleIcon}
								title={i18n._(LOAD_SESSIONS_FAILED_DESCRIPTOR)}
								description={store.listError}
								actions={[{text: i18n._(TRY_AGAIN_DESCRIPTOR), onClick: () => void store.loadSessions(true)}]}
								data-flx="discovery.companion-records.list-error"
							/>
						) : (
							<>
								{store.listError ? (
									<div
										className={recordStyles.errorBanner}
										role="alert"
										data-flx="discovery.companion-records.list-refresh-error"
									>
										<span data-flx="discovery.discovery.discovery-companion-records-page.span">{store.listError}</span>
										<Button
											variant="secondary"
											small
											fitContent
											onClick={() => void store.loadSessions(true)}
											data-flx="discovery.discovery.discovery-companion-records-page.button"
										>
											{i18n._(TRY_AGAIN_DESCRIPTOR)}
										</Button>
									</div>
								) : null}
								<CompanionSessionsList
									sceneNames={sceneNames}
									sessions={store.sessions}
									onSelectSession={handleSelectSession}
									data-flx="discovery.discovery.discovery-companion-records-page.companion-sessions-list"
								/>
							</>
						)}
					</section>
				</div>
			</Scroller>
		</div>
	);
});
